import React from 'react';
import { CardViewContentBlockProps } from '../types';
import { Box, Typography } from '@mui/material';
import CategoryCard from '../../../common/category-card/CategoryCard';
import CategoryCardGrid from '../../../common/category-card/CategoryCardGrid';
import { CATEGORY_CARD_ACCENTS } from '../../../common/category-card/types';
import RegistrationIcon from '../../../../assets/svg/category-registration.svg?react';
import PolicyIcon from '../../../../assets/svg/policy.svg?react';
import OwnershipIcon from '../../../../assets/svg/manual-doc.svg?react';
import SubscriptionIcon from '../../../../assets/svg/box.svg?react';
import OperationsIcon from '../../../../assets/svg/business-to-consumer-plan.svg?react';
import AccountingIcon from '../../../../assets/svg/view-list.svg?react';
import { useTranslation } from 'react-i18next';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import DocumentListVault from '../../document-vault/components/document-list-vault/DocumentListVault';
import DocumentsBlock, { DocumentsActions } from '../../../common/documents-block/DocumentsBlock';
import styles from '../BusinessProfile.module.scss';
import InsuranceIcon from '../../../../assets/svg/insurance-licenses.svg?react';
import AssetsIcon from '../../../../assets/svg/list_alt_check.svg?react';
import { ProgressOverlay } from '../../../common/progress-overlay/ProgressOverlay';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { RootState } from '../../../store';
import { useDocumentsMetaData } from '../../document-vault/hooks/useDocumentsWorkflowsMetadata';
import { clearDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';

export const BusinessCategoryContent = ({
  documents,
  workflow,
  setWorkflow,
  showWorkflowBreadcrumbs,
  setShowWorkflowBreadcrumbs,
  cardView,
  documentsActions,
}: CardViewContentBlockProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const documentUploadsLoading = useAppSelector((state: RootState) => state.documents.status);

  // Use metadata hook for business category
  const { uploadedDocumentsMetaData } = useDocumentsMetaData('All', 'business');

  const getDocsFilteredByEmptyWorkflowCount = () => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === '');
    return metaData ? metaData.count : 0;
  };

  const getDocsCount = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData
      ? metaData.count +
          (workflow === t('labels.generalAdministration')
            ? getDocsFilteredByEmptyWorkflowCount()
            : 0)
      : 0;
  };

  const hasNewDocuments = (workflow: string) => {
    const metaData = uploadedDocumentsMetaData.find((el) => el.workflow === workflow);
    return metaData ? metaData.isNew : false;
  };

  const getIcon = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.company.registration'):
        return <RegistrationIcon />;
      case t('tagsLabels.documentWorkflow.company.agreements'):
        return <PolicyIcon />;
      case t('tagsLabels.documentWorkflow.company.management'):
        return <OwnershipIcon />;
      case t('tagsLabels.documentWorkflow.company.subscriptions'):
        return <SubscriptionIcon />;
      case t('tagsLabels.documentWorkflow.company.generalAdministration'):
        return <OperationsIcon />;
      case t('tagsLabels.documentWorkflow.company.accounting'):
        return <AccountingIcon />;
      case t('tagsLabels.documentWorkflow.company.insuranceAndLicenses'):
        return <InsuranceIcon />;
      case t('tagsLabels.documentWorkflow.company.assets'):
        return <AssetsIcon />;
    }
  };

  const getWorkflowNumber = (workflow: string) => {
    switch (workflow) {
      case t('tagsLabels.documentWorkflow.company.registration'):
        return '1. ';
      case t('tagsLabels.documentWorkflow.company.accounting'):
        return '2. ';
      case t('tagsLabels.documentWorkflow.company.agreements'):
        return '3. ';
      case t('tagsLabels.documentWorkflow.company.generalAdministration'):
        return '4. ';
      case t('tagsLabels.documentWorkflow.company.insuranceAndLicenses'):
        return '5. ';
      case t('tagsLabels.documentWorkflow.company.assets'):
        return '6. ';
    }
  };

  return (
    <>
      {/* Workflows subheader + documents actions (workflow cards view) in one row. */}
      {((!workflow && !showWorkflowBreadcrumbs) ||
        (documentsActions && !(workflow && showWorkflowBreadcrumbs))) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          {!workflow && !showWorkflowBreadcrumbs && (
            <Typography
              variant='h3'
              component='h3'
              sx={{ margin: 0, fontSize: '15px', lineHeight: '24px' }}
            >
              {t('headers.workflows')}
            </Typography>
          )}
          {documentsActions && !(workflow && showWorkflowBreadcrumbs) && (
            <DocumentsActions {...documentsActions} standalone />
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
          paddingBottom: '24px',
        }}
      >
        {workflow && showWorkflowBreadcrumbs ? (
          <>
            <Box className={styles.breadcrumbs}>
              <Typography
                id='businessProfileBusinessCategoryContentWorkflowsLink'
                variant='h3'
                component='h3'
                className={styles.documentViewText}
                onClick={() => {
                  setShowWorkflowBreadcrumbs(false);
                  setWorkflow(null);
                  dispatch(clearDocuments());
                }}
              >
                {t('headers.workflows')}
              </Typography>
              <Typography variant='body2' className={styles.separator}>
                ›
              </Typography>
              <Typography variant='body2' className={styles.documentViewText}>
                {workflow === t('headers.general') ? t('labels.generalUnassigned') : workflow}
              </Typography>
            </Box>
            {/* Open workflow: the documents actions are attached to the table. */}
            <Box style={{ position: 'relative', width: '100%' }}>
              <DocumentsBlock actions={documentsActions}>
                <DocumentListVault
                  cardView={cardView}
                  compact
                  embedded={!!documentsActions}
                  documents={[...documents] /*getDocsFilteredByWorkflow(workflow)*/}
                />
                {documentUploadsLoading === 'loading' && <ProgressOverlay position='absolute' />}
              </DocumentsBlock>
            </Box>
          </>
        ) : (
          <CategoryCardGrid id='businessProfileBusinessCategoryContentGrid'>
            {TAGS_LABELS.documentWorkflow.business.map((el) => (
              <CategoryCard
                id={`businessProfileBusinessCategoryContentBusinessCard_${el}`}
                title={getWorkflowNumber(t(el)) + t(el)}
                countLabel={t(
                  getDocsCount(t(el)) === 1
                    ? 'categoryCards.documentCountSingular'
                    : 'categoryCards.documentCountPlural',
                  { count: getDocsCount(t(el)) }
                )}
                accent={CATEGORY_CARD_ACCENTS.Business}
                onClick={() => {
                  setWorkflow(t(el));
                  setShowWorkflowBreadcrumbs(true);
                }}
                icon={getIcon(t(el))}
                key={el}
                hasNewLabel={
                  el === 'tagsLabels.documentWorkflow.company.generalAdministration'
                    ? hasNewDocuments(t(el)) || hasNewDocuments('')
                    : hasNewDocuments(t(el))
                }
              />
            ))}
          </CategoryCardGrid>
        )}
      </Box>
    </>
  );
};
